import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Camera, Image, Plus, QrCode, Users, Video, Loader2, X, Film } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { EventCheckIn } from "./types";

interface Props {
  checkIn: EventCheckIn;
  onUpdateCheckIn: (updates: Partial<EventCheckIn>) => void;
}

function MediaThumbnail({ path, type, onDelete }: { path: string; type: "image" | "video"; onDelete: () => void }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    supabase.storage.from("event-photos").createSignedUrl(path, 3600).then(({ data }) => {
      if (data?.signedUrl) setUrl(data.signedUrl);
    });
  }, [path]);

  return (
    <div className="relative w-16 h-16 rounded border bg-muted/50 overflow-hidden flex-shrink-0">
      {type === "image" && url ? (
        <img src={url} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Film className="h-5 w-5 text-muted-foreground" />
        </div>
      )}
      <button
        onClick={onDelete}
        className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

export function EventTasksCard({ checkIn, onUpdateCheckIn }: Props) {
  const [saving, setSaving] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactCompany, setContactCompany] = useState("");
  const [contactNotes, setContactNotes] = useState("");
  const [uploading, setUploading] = useState<string | null>(null);
  const [checkingDupe, setCheckingDupe] = useState(false);

  const updateField = async (field: string, value: any) => {
    setSaving(true);
    onUpdateCheckIn({ [field]: value } as any);
    try {
      const { error } = await supabase.from("event_check_ins").update({ [field]: value }).eq("id", checkIn.id);
      if (error) throw error;
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addContact = async () => {
    if (!contactName.trim() || !contactPhone.trim()) return;
    const phone = contactPhone.trim();

    // Check for duplicate in vendor_contacts
    setCheckingDupe(true);
    try {
      const { data: existing } = await supabase
        .from("vendor_contacts")
        .select("id")
        .eq("phone", phone)
        .limit(1);
      if (existing && existing.length > 0) {
        toast.error("This contact is already in our directory.");
        setCheckingDupe(false);
        return;
      }
    } catch {
      // proceed if check fails
    }
    setCheckingDupe(false);

    const newContact = {
      name: contactName.trim(),
      phone,
      email: contactEmail.trim() || undefined,
      company: contactCompany.trim() || undefined,
      notes: contactNotes.trim() || undefined,
    };
    const updated = [...(checkIn.contacts_gathered || []), newContact];
    await updateField("contacts_gathered", updated);
    setContactName(""); setContactPhone(""); setContactEmail(""); setContactCompany(""); setContactNotes("");
    toast.success("Contact added");
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: "qr" | "reel" | "team" | "video") => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(type);
    try {
      const path = `${checkIn.id}/${type}_${Date.now()}_${file.name}`;
      const { error } = await supabase.storage.from("event-photos").upload(path, file);
      if (error) throw error;

      if (type === "qr") {
        await updateField("qr_photo_path", path);
      } else if (type === "reel") {
        const updated = [...(checkIn.reel_storage_paths || []), path];
        await updateField("reel_storage_paths", updated);
      } else if (type === "team") {
        const updated = [...(checkIn.team_photo_paths || []), path];
        await updateField("team_photo_paths", updated);
      } else {
        const updated = [...(checkIn.video_storage_paths || []), path];
        await updateField("video_storage_paths", updated);
      }
      toast.success("Uploaded!");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  const deleteMedia = async (path: string, field: string, currentPaths: string[]) => {
    const updated = currentPaths.filter(p => p !== path);
    await updateField(field, updated);
    await supabase.storage.from("event-photos").remove([path]);
    toast.success("Removed");
  };

  const deleteQrPhoto = async () => {
    if (checkIn.qr_photo_path) {
      await supabase.storage.from("event-photos").remove([checkIn.qr_photo_path]);
    }
    await updateField("qr_photo_path", null);
  };

  const contacts = checkIn.contacts_gathered || [];
  const reels = checkIn.reel_storage_paths || [];
  const teamPhotos = checkIn.team_photo_paths || [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Event Tasks</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* QR Codes - auto-detect distribution from photo */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-sm mb-1">
            <QrCode className="h-3.5 w-3.5" />
            QR Code Business Cards
            {checkIn.qr_photo_path && (
              <Badge className="bg-green-500/15 text-green-700 border-green-500/30 text-[9px] ml-1">Distributed ✓</Badge>
            )}
          </div>

          {checkIn.qr_photo_path ? (
            <div className="flex items-center gap-2">
              <MediaThumbnail path={checkIn.qr_photo_path} type="image" onDelete={deleteQrPhoto} />
              <span className="text-[10px] text-muted-foreground">QR card photo</span>
            </div>
          ) : (
            <label className="cursor-pointer block">
              <input type="file" accept="image/*" className="hidden" onChange={e => handleFileUpload(e, "qr")} />
              <Button size="sm" variant="outline" className="text-xs h-7" asChild disabled={uploading === "qr"}>
                <span>{uploading === "qr" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Camera className="h-3 w-3 mr-1" />}Upload QR Photo</span>
              </Button>
            </label>
          )}
        </div>

        {/* Marketing Reels */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-sm">
            <Video className="h-3.5 w-3.5" />
            Marketing Reels
            <Badge variant="outline" className="text-[10px] ml-1">{reels.length}/1</Badge>
          </div>
          <p className="text-[9px] text-muted-foreground">Max 2 minutes per reel • up to 3 allowed</p>
          {reels.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {reels.map((path, i) => (
                <MediaThumbnail
                  key={path}
                  path={path}
                  type="video"
                  onDelete={() => deleteMedia(path, "reel_storage_paths", reels)}
                />
              ))}
            </div>
          )}
          {reels.length < 3 && (
            <label className="cursor-pointer block">
              <input type="file" accept="video/*" className="hidden" onChange={e => handleFileUpload(e, "reel")} />
              <Button size="sm" variant="outline" className="text-xs h-7" asChild disabled={uploading === "reel"}>
                <span>{uploading === "reel" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}Upload Reel</span>
              </Button>
            </label>
          )}
        </div>

        {/* Team Photos */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-sm">
            <Image className="h-3.5 w-3.5" />
            Team Photos
            <Badge variant="outline" className="text-[10px] ml-1">{teamPhotos.length}/1</Badge>
          </div>
          {teamPhotos.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {teamPhotos.map((path) => (
                <MediaThumbnail
                  key={path}
                  path={path}
                  type="image"
                  onDelete={() => deleteMedia(path, "team_photo_paths", teamPhotos)}
                />
              ))}
            </div>
          )}
          <label className="cursor-pointer block">
            <input type="file" accept="image/*" className="hidden" onChange={e => handleFileUpload(e, "team")} />
            <Button size="sm" variant="outline" className="text-xs h-7" asChild disabled={uploading === "team"}>
              <span>{uploading === "team" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Camera className="h-3 w-3 mr-1" />}Upload Photo</span>
            </Button>
          </label>
        </div>

        {/* Gather Contacts */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-sm">
            <Users className="h-3.5 w-3.5" />
            Gather Contacts ({contacts.length}/1)
          </div>
          {contacts.map((c, i) => (
            <div key={i} className="p-1.5 bg-muted/50 rounded text-xs">
              <span className="font-medium">{c.name}</span> — {c.phone}
              {c.company && <span className="text-muted-foreground ml-1">({c.company})</span>}
              {c.email && <span className="text-muted-foreground ml-1">• {c.email}</span>}
            </div>
          ))}
          {contacts.length < 3 && (
            <div className="space-y-1.5">
              <div className="flex gap-1.5">
                <Input placeholder="Name *" value={contactName} onChange={e => setContactName(e.target.value)} className="h-7 text-xs" />
                <Input placeholder="Phone *" value={contactPhone} onChange={e => setContactPhone(e.target.value)} className="h-7 text-xs" />
              </div>
              <div className="flex gap-1.5">
                <Input placeholder="Email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="h-7 text-xs" />
                <Input placeholder="Company/Role" value={contactCompany} onChange={e => setContactCompany(e.target.value)} className="h-7 text-xs" />
              </div>
              <Textarea placeholder="Notes (optional)" value={contactNotes} onChange={e => setContactNotes(e.target.value)} rows={1} className="text-xs" />
              <Button size="sm" className="h-7 text-xs w-full" onClick={addContact} disabled={saving || checkingDupe}>
                {checkingDupe ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                Add Contact
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
